<template>
  <template v-if="hasAnyParam">
    <div class="dkn-admin-statuses mt-2">
      <div
        v-for="status in quickStatuses"
        :key="status.key"
        class="dkn-admin-status"
        :class="status.toneClass"
      >
        <div class="dkn-admin-status-header">
          <i :class="status.icon" />
          <span>{{ status.label }}</span>
        </div>
        <div class="dkn-admin-status-value">
          {{ status.value }}
        </div>
      </div>
    </div>

    <div
      v-if="temperatureItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-thermometer-half" />
        <span>Temperatury</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in temperatureItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-thermometer-half" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="statusItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-toggles" />
        <span>Statusy</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in statusItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-circle-half" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="alarmItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-exclamation-triangle" />
        <span>Alarmy</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in alarmItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-exclamation-circle" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="ioItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-diagram-3" />
        <span>Wejścia / wyjścia</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in ioItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-arrow-left-right" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>
  </template>

  <div
    v-else
    class="modbus-empty"
  >
    <i class="bi bi-thermometer-snow" />
    <span>Brak danych chłodni</span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

const MPS_PREFIX = 'DKN_MPS_'
type Tone = 'neutral' | 'ok' | 'warn' | 'bad'

const QUICK_STATUS_KEYS = new Set([
  'DKN_MPS_STATUS_ON_OFF',
  'DKN_MPS_PROBE_1',
  'DKN_MPS_STATUS_DEFROST',
  'DKN_MPS_DI_DOOR_STATUS',
])

const TEMPERATURE_KEYS = new Set([
  'DKN_MPS_PROBE_1',
  'DKN_MPS_PROBE_2',
  'DKN_MPS_PROBE_3',
  'DKN_MPS_PROBE_4',
  'DKN_MPS_DISPLAYED_PROBE',
  'DKN_MPS_TEMP_REGULATION_PROBE',
  'DKN_MPS_TEMP_ALARM_1_PROBE',
  'DKN_MPS_TEMP_ALARM_2_PROBE',
  'DKN_MPS_REAL_REGULATION_SETPOINT',
  'DKN_MPS_SETPOINT_ACTIVE_MAP',
  'DKN_MPS_DEFROST_1_CONTROL_PROBE',
  'DKN_MPS_DEFROST_2_CONTROL_PROBE',
  'DKN_MPS_EVAP_FAN_CONTROL_PROBE',
  'DKN_MPS_EVAP_FAN_STOP_THRESHOLD',
  'DKN_MPS_HIGH_TEMP_1_ALARM_THRESHOLD',
  'DKN_MPS_HIGH_TEMP_2_ALARM_THRESHOLD',
  'DKN_MPS_LOW_TEMP_1_ALARM_THRESHOLD',
  'DKN_MPS_LOW_TEMP_2_ALARM_THRESHOLD',
])

const ALARM_KEYS = new Set([
  'DKN_MPS_ALARM_DOOR_OPEN',
  'DKN_MPS_ALARM_EXTERNAL_ALARM_EAL',
  'DKN_MPS_ALARM_EXTERNAL_LOCKOUT_BAL',
  'DKN_MPS_ALARM_HIGH_TEMP_1',
  'DKN_MPS_ALARM_INTERNAL_MEMORY_FAILURE',
  'DKN_MPS_ALARM_LOW_TEMP_1',
  'DKN_MPS_ALARM_PRESSURE_SWITCH_PAL',
  'DKN_MPS_ALARM_PROBE_P1_ERROR',
  'DKN_MPS_ALARM_PROBE_P2_ERROR',
  'DKN_MPS_ALARM_PROBE_P3_ERROR',
  'DKN_MPS_ALARM_PROBE_P4_ERROR',
  'DKN_MPS_ALARM_RTC_FAILURE',
])

const STATUS_KEYS = new Set([
  'DKN_MPS_STATUS_ON_OFF',
  'DKN_MPS_STATUS_DEFROST',
  'DKN_MPS_STATUS_COOLING_REQUEST',
  'DKN_MPS_STATUS_ENERGY_SAVING',
  'DKN_MPS_STATUS_LIGHT',
  'DKN_MPS_STATUS_AUXILIARY',
  'DKN_MPS_STATUS_BUZZER',
  'DKN_MPS_STATUS_FAN_CHANGE',
  'DKN_MPS_STATUS_KEYBOARD_LOCK',
  'DKN_MPS_STATUS_DEFROST_STOPPED_BY_TIME',
  'DKN_MPS_STATUS_DRIPPING_TIME_AFTER_DEFROST',
  'DKN_MPS_STATUS_TIMEOUT_TO_REACH_SET_AFTER_DEFROST',
  'DKN_MPS_OUTPUT_TEST_MODE_STATUS',
])

const IO_KEYS = new Set([
  'DKN_MPS_DI_DEFROST_STATUS',
  'DKN_MPS_DI_DOOR_STATUS',
  'DKN_MPS_DI_ENERGY_SAVING_STATUS',
  'DKN_MPS_DI_EXTERNAL_ALARM_STATUS',
  'DKN_MPS_DI_LIGHT_STATUS',
  'DKN_MPS_DI_ON_OFF_STATUS',
  'DKN_MPS_DI_PRESSURE_SWITCH_STATUS',
  'DKN_MPS_DO_ALARM_OUTPUT_STATUS',
  'DKN_MPS_DO_COMPRESSOR_OUTPUT_1',
  'DKN_MPS_DO_COMPRESSOR_OUTPUT_2',
  'DKN_MPS_DO_DEFROST_OUTPUT_1',
  'DKN_MPS_DO_EVAP_FAN_OUTPUT_STATUS',
  'DKN_MPS_DO_LIGHT_OUTPUT_STATUS',
  'DKN_MPS_OA1_OUTPUT',
  'DKN_MPS_OA2_OUTPUT',
  'DKN_MPS_OA3_OUTPUT',
  'DKN_MPS_OA4_OUTPUT',
  'DKN_MPS_OA5_OUTPUT',
])

const PARAM_LABELS: Record<string, string> = {
  DKN_MPS_STATUS_ON_OFF: 'Włączenie',
  DKN_MPS_PROBE_1: 'Temp. komory (P1)',
  DKN_MPS_PROBE_2: 'Sonda odszraniania (P2)',
  DKN_MPS_PROBE_3: 'Sonda 3',
  DKN_MPS_PROBE_4: 'Sonda 4',
  DKN_MPS_DISPLAYED_PROBE: 'Wyświetlana temperatura',
  DKN_MPS_TEMP_REGULATION_PROBE: 'Sonda regulacji',
  DKN_MPS_TEMP_ALARM_1_PROBE: 'Sonda alarmu temp. 1',
  DKN_MPS_TEMP_ALARM_2_PROBE: 'Sonda alarmu temp. 2',
  DKN_MPS_REAL_REGULATION_SETPOINT: 'Nastawa regulacji',
  DKN_MPS_SETPOINT_ACTIVE_MAP: 'Aktywna nastawa',
  DKN_MPS_DEFROST_1_CONTROL_PROBE: 'Sonda kontroli odszraniania 1',
  DKN_MPS_DEFROST_2_CONTROL_PROBE: 'Sonda kontroli odszraniania 2',
  DKN_MPS_EVAP_FAN_CONTROL_PROBE: 'Sonda wentylatora parownika',
  DKN_MPS_EVAP_FAN_STOP_THRESHOLD: 'Próg zatrzymania wentylatora',
  DKN_MPS_HIGH_TEMP_1_ALARM_THRESHOLD: 'Próg alarmu temp. high 1',
  DKN_MPS_HIGH_TEMP_2_ALARM_THRESHOLD: 'Próg alarmu temp. high 2',
  DKN_MPS_LOW_TEMP_1_ALARM_THRESHOLD: 'Próg alarmu temp. low 1',
  DKN_MPS_LOW_TEMP_2_ALARM_THRESHOLD: 'Próg alarmu temp. low 2',
  DKN_MPS_STATUS_DEFROST: 'Odszranianie',
  DKN_MPS_STATUS_COOLING_REQUEST: 'Żądanie chłodzenia',
  DKN_MPS_STATUS_ENERGY_SAVING: 'Oszczędzanie energii',
  DKN_MPS_STATUS_LIGHT: 'Oświetlenie',
  DKN_MPS_STATUS_AUXILIARY: 'Status pomocniczy',
  DKN_MPS_STATUS_BUZZER: 'Buzzer',
  DKN_MPS_STATUS_FAN_CHANGE: 'Zmiana wentylatora',
  DKN_MPS_STATUS_KEYBOARD_LOCK: 'Blokada klawiatury',
  DKN_MPS_STATUS_DEFROST_STOPPED_BY_TIME: 'Odszran. zakończone czasowo',
  DKN_MPS_STATUS_DRIPPING_TIME_AFTER_DEFROST: 'Czas kapania po odszran.',
  DKN_MPS_STATUS_TIMEOUT_TO_REACH_SET_AFTER_DEFROST: 'Timeout do nastawy po odszran.',
  DKN_MPS_OUTPUT_TEST_MODE_STATUS: 'Tryb testowy wyjść',
  DKN_MPS_ALARM_DOOR_OPEN: 'Alarm: drzwi otwarte',
  DKN_MPS_ALARM_EXTERNAL_ALARM_EAL: 'Alarm zewnętrzny (EAL)',
  DKN_MPS_ALARM_EXTERNAL_LOCKOUT_BAL: 'Blokada zewnętrzna (BAL)',
  DKN_MPS_ALARM_HIGH_TEMP_1: 'Alarm: temp. za wysoka',
  DKN_MPS_ALARM_INTERNAL_MEMORY_FAILURE: 'Błąd pamięci (EE)',
  DKN_MPS_ALARM_LOW_TEMP_1: 'Alarm: temp. za niska',
  DKN_MPS_ALARM_PRESSURE_SWITCH_PAL: 'Alarm presostatu (PAL)',
  DKN_MPS_ALARM_PROBE_P1_ERROR: 'Błąd sondy P1',
  DKN_MPS_ALARM_PROBE_P2_ERROR: 'Błąd sondy P2',
  DKN_MPS_ALARM_PROBE_P3_ERROR: 'Błąd sondy P3',
  DKN_MPS_ALARM_PROBE_P4_ERROR: 'Błąd sondy P4',
  DKN_MPS_ALARM_RTC_FAILURE: 'Błąd RTC',
  DKN_MPS_DI_DEFROST_STATUS: 'Wej. odszraniania',
  DKN_MPS_DI_DOOR_STATUS: 'Drzwi',
  DKN_MPS_DI_ENERGY_SAVING_STATUS: 'Wej. oszcz. energii',
  DKN_MPS_DI_EXTERNAL_ALARM_STATUS: 'Wej. alarmu zewn.',
  DKN_MPS_DI_LIGHT_STATUS: 'Wej. oświetlenia',
  DKN_MPS_DI_ON_OFF_STATUS: 'Wej. wł./wył.',
  DKN_MPS_DI_PRESSURE_SWITCH_STATUS: 'Wej. presostatu',
  DKN_MPS_DO_ALARM_OUTPUT_STATUS: 'Wyj. alarmu',
  DKN_MPS_DO_COMPRESSOR_OUTPUT_1: 'Sprężarka 1',
  DKN_MPS_DO_COMPRESSOR_OUTPUT_2: 'Sprężarka 2',
  DKN_MPS_DO_DEFROST_OUTPUT_1: 'Wyj. odszraniania 1',
  DKN_MPS_DO_EVAP_FAN_OUTPUT_STATUS: 'Wentylator parownika',
  DKN_MPS_DO_LIGHT_OUTPUT_STATUS: 'Wyj. oświetlenia',
  DKN_MPS_OA1_OUTPUT: 'Wyj. OA1 (sprężarka/skrapl.)',
  DKN_MPS_OA2_OUTPUT: 'Wyj. OA2 (went. parownika)',
  DKN_MPS_OA3_OUTPUT: 'Wyj. OA3 (oświetlenie)',
  DKN_MPS_OA4_OUTPUT: 'Wyj. OA4 (odszranianie)',
  DKN_MPS_OA5_OUTPUT: 'Wyj. OA5 (alarm)',
}

function toNumber(raw: any): number | null {
  if (raw === null || raw === undefined || raw === '') return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  const parsed = parseFloat(String(raw))
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeValue(raw: any): string {
  if (raw === null || raw === undefined || raw === '') return '—'
  if (typeof raw === 'number') return Number.isInteger(raw) ? raw.toString() : raw.toFixed(2)
  const parsed = parseFloat(raw)
  if (Number.isFinite(parsed)) return Number.isInteger(parsed) ? parsed.toString() : parsed.toFixed(2)
  return String(raw)
}

function toneClass(tone: Tone): string {
  return `dkn-tone-${tone}`
}

function getParamTone(name: string, rawValue: any): Tone {
  const value = toNumber(rawValue)
  if (value === null) return 'neutral'

  if (ALARM_KEYS.has(name)) return value > 0 ? 'bad' : 'ok'

  if (name === 'DKN_MPS_STATUS_ON_OFF') return value > 0 ? 'ok' : 'bad'
  if (name === 'DKN_MPS_STATUS_DEFROST') return value > 0 ? 'warn' : 'ok'
  if (name === 'DKN_MPS_DI_DOOR_STATUS') return value > 0 ? 'bad' : 'ok'
  if (name === 'DKN_MPS_STATUS_COOLING_REQUEST') return value > 0 ? 'warn' : 'ok'

  if (name === 'DKN_MPS_PROBE_1' || name === 'DKN_MPS_DISPLAYED_PROBE' || name === 'DKN_MPS_TEMP_REGULATION_PROBE') {
    if (value > 10) return 'bad'
    if (value > 5) return 'warn'
    return 'ok'
  }

  return 'neutral'
}

interface ParamItem {
  key: string
  label: string
  value: string
  toneClass: string
}

export default defineComponent({
  name: 'DknMpsLatestParamsPanel',
  props: {
    latestParams: {
      type: Array,
      required: true
    }
  },
  computed: {
    mpsByName(): Record<string, any> {
      const params = Array.isArray(this.latestParams) ? this.latestParams : []
      const seen = new Set<string>()
      return params
        .filter((p: any) => {
          const name = p?.can_params_io?.param_name
          if (typeof name !== 'string' || !name.startsWith(MPS_PREFIX) || seen.has(name)) return false
          seen.add(name)
          return true
        })
        .reduce((acc: Record<string, any>, p: any) => {
          acc[p.can_params_io.param_name] = p
          return acc
        }, {})
    },
    hasAnyParam(): boolean {
      return Object.keys(this.mpsByName).length > 0
    },
    activeAlarmCount(): number {
      return Object.entries(this.mpsByName).filter(([name, p]) => {
        return ALARM_KEYS.has(name) && (toNumber((p as any)?.value) ?? 0) > 0
      }).length
    },
    quickStatuses(): Array<{ key: string; label: string; value: string; icon: string; toneClass: string }> {
      const build = (
        key: string,
        label: string,
        icon: string,
        formatter: (raw: any) => string,
        toneOverride?: Tone
      ) => {
        const raw = this.mpsByName[key]?.value
        const tone = toneOverride ?? getParamTone(key, raw)
        return { key, label, icon, value: formatter(raw), toneClass: toneClass(tone) }
      }

      const alarmTone: Tone = this.activeAlarmCount > 0 ? 'bad' : 'ok'

      return [
        build('DKN_MPS_STATUS_ON_OFF', 'Stan', 'bi bi-power', (raw) => {
          const n = toNumber(raw)
          if (n === null) return '—'
          return n > 0 ? 'Włączona' : 'Wyłączona'
        }),
        build('DKN_MPS_PROBE_1', 'Temp. komory', 'bi bi-thermometer-half', (raw) => {
          const n = toNumber(raw)
          if (n === null) return '—'
          return `${n.toFixed(1)} °C`
        }),
        build('DKN_MPS_STATUS_DEFROST', 'Odszranianie', 'bi bi-snow2', (raw) => {
          const n = toNumber(raw)
          if (n === null) return '—'
          return n > 0 ? 'Aktywne' : 'Nie'
        }),
        {
          key: '__alarms__',
          label: 'Aktywne alarmy',
          icon: 'bi bi-exclamation-triangle',
          value: this.activeAlarmCount > 0 ? String(this.activeAlarmCount) : 'Brak',
          toneClass: toneClass(alarmTone)
        },
        build('DKN_MPS_DI_DOOR_STATUS', 'Drzwi', 'bi bi-door-open', (raw) => {
          const n = toNumber(raw)
          if (n === null) return '—'
          return n > 0 ? 'Otwarte' : 'Zamknięte'
        }),
      ]
    },
    temperatureItems(): ParamItem[] {
      return this.buildItems(TEMPERATURE_KEYS, (name, raw) => {
        const n = toNumber(raw)
        if (n === null) return '—'
        return `${n.toFixed(1)} °C`
      })
    },
    statusItems(): ParamItem[] {
      return this.buildItems(STATUS_KEYS, (name, raw) => {
        const n = toNumber(raw)
        if (n === null) return '—'
        return n > 0 ? 'Tak' : 'Nie'
      })
    },
    alarmItems(): ParamItem[] {
      return this.buildItems(ALARM_KEYS, (name, raw) => {
        const n = toNumber(raw)
        if (n === null) return '—'
        return n > 0 ? 'Aktywny' : 'Brak'
      })
    },
    ioItems(): ParamItem[] {
      return this.buildItems(IO_KEYS, (name, raw) => {
        const n = toNumber(raw)
        if (n === null) return '—'
        return n > 0 ? 'Aktywny' : 'Nieaktywny'
      })
    },
  },
  methods: {
    buildItems(
      keySet: Set<string>,
      formatter: (name: string, raw: any) => string
    ): ParamItem[] {
      return [...keySet]
        .filter((name) => name in this.mpsByName)
        .map((name) => {
          const p = this.mpsByName[name]
          const raw = p?.value
          return {
            key: `${name}-${p?.id ?? ''}`,
            label: PARAM_LABELS[name] ?? name,
            value: formatter(name, raw),
            toneClass: toneClass(getParamTone(name, raw))
          }
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    }
  }
})
</script>

<style scoped lang="scss">
.dkn-admin-statuses {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}

.dkn-admin-status,
.dkn-param-card {
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-left: 4px solid rgba(148, 163, 184, 0.65);
  border-radius: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.55);
}

.dkn-admin-status-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
}

.dkn-admin-status-value {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
}

.asset-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
}

.dkn-mps-section {
  margin-top: 14px;
}

.dkn-mps-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.modbus-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  color: #94a3b8;
  font-size: 14px;

  i {
    font-size: 28px;
  }
}

.dkn-tone-ok {
  border-left-color: rgba(22, 163, 74, 0.9) !important;

  .dkn-admin-status-value {
    color: #166534;
  }
}

.dkn-tone-warn {
  border-left-color: rgba(234, 179, 8, 0.95) !important;

  .dkn-admin-status-value {
    color: #92400e;
  }
}

.dkn-tone-bad {
  border-left-color: rgba(220, 38, 38, 0.95) !important;

  .dkn-admin-status-value {
    color: #991b1b;
  }
}
</style>
